/**
 * gridNavigation — pure functions for keyboard navigation in the IE/DS grades grid.
 *
 * Grid structure: rows = evaluations (n_ie + n_ds total rows).
 * Columns: 0 = IE score cells, 1 = DS score cells (one column per type).
 *
 * Tab/Enter: advance to next cell left-to-right, top-to-bottom.
 *   At last cell of a row: wrap to first cell of next row.
 *   At last cell of last row: stay on last cell.
 * Shift+Tab: move backwards.
 * ArrowRight / ArrowLeft / ArrowUp / ArrowDown: directional move.
 * Escape: no move (returns null — caller should blur).
 */

/**
 * @typedef {Object} Cell
 * @property {number} row  - 0-indexed row
 * @property {number} col  - 0-indexed column
 */

/**
 * Compute the next cell given the current position and a keyboard event key.
 *
 * @param {Cell} current       - current cell {row, col}
 * @param {string} key         - e.key value ('Tab', 'Enter', 'ArrowRight', ...)
 * @param {boolean} shiftKey   - true when Shift is held
 * @param {number} rowCount    - total number of rows (>= 1)
 * @param {number} colCount    - total number of columns (>= 1)
 * @returns {Cell|null}        - next cell, or null when focus should not move
 *                              (null on Escape; out-of-bounds clamped to edge)
 */
export function nextCell(current, key, shiftKey, rowCount, colCount) {
  const { row, col } = current;
  const lastRow = rowCount - 1;
  const lastCol = colCount - 1;

  if (key === 'Escape') {
    return null;
  }

  if (key === 'Tab') {
    if (shiftKey) {
      // Shift+Tab: go backwards
      if (col > 0) return { row, col: col - 1 };
      if (row > 0) return { row: row - 1, col: lastCol };
      return { row: 0, col: 0 };
    }
    // Tab: advance
    if (col < lastCol) return { row, col: col + 1 };
    if (row < lastRow) return { row: row + 1, col: 0 };
    return { row: lastRow, col: lastCol };
  }

  if (key === 'Enter') {
    // Enter: same as Tab (advance forward)
    if (col < lastCol) return { row, col: col + 1 };
    if (row < lastRow) return { row: row + 1, col: 0 };
    return { row: lastRow, col: lastCol };
  }

  if (key === 'ArrowRight') {
    return { row, col: Math.min(col + 1, lastCol) };
  }

  if (key === 'ArrowLeft') {
    return { row, col: Math.max(col - 1, 0) };
  }

  if (key === 'ArrowDown') {
    return { row: Math.min(row + 1, lastRow), col };
  }

  if (key === 'ArrowUp') {
    return { row: Math.max(row - 1, 0), col };
  }

  return null;
}

/**
 * Build a deterministic cell id string for use as a data-cell-id attribute.
 * @param {number} row
 * @param {number} col
 * @returns {string}
 */
export function cellId(row, col) {
  return `cell-${row}-${col}`;
}
